// Generated by TaroBase SDK Generator: 2026-04-14T11:42:05.668Z
// Collection: fanArtComments/$commentId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Public comments on fan art submissions. Anonymous-friendly, anyone can post. Admin and founder can delete for moderation.
 */

export interface FanArtCommentsRequest {
  submissionId: string;
  displayName: string;
  message: string;
  walletAddress?: string;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface FanArtCommentsResponse {
  submissionId: string;
  displayName: string;
  message: string;
  walletAddress?: string;
  createdAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a FanArtComments operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildFanArtComments(commentId: string, data?: FanArtCommentsRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `fanArtComments/${commentId}`, document: doc };
}

/**
 * Anyone can post a comment with no authentication required. displayName and message are required. walletAddress is optional (set if the user has a connected wallet). submissionId, displayName, message, and createdAt are immutable after creation. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setFanArtComments(commentId: string, data?: FanArtCommentsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`fanArtComments/${commentId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting FanArtComments: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read fan art comments. Displayed publicly under each fan art submission.
   (Get Single Item)
 */
export async function getFanArtComments(commentId: string): Promise<FanArtCommentsResponse | null> {
  try {
    const result = await get(`fanArtComments/${commentId}`);
    return result as FanArtCommentsResponse | null;
  } catch (error) {
    console.error(`Error getting FanArtComments: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single FanArtComments document. (
  Read Operation Details: Anyone can read fan art comments. Displayed publicly under each fan art submission.
  )
 */
export function subscribeFanArtComments(callback: (data: FanArtCommentsResponse | null) => void, commentId: string): Promise<() => Promise<void>> {
  return subscribe(`fanArtComments/${commentId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as FanArtCommentsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to FanArtComments at path '${`fanArtComments/${commentId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many FanArtComments items from collection fanArtComments
 
  Read Operation Details: Anyone can read fan art comments. Displayed publicly under each fan art submission.
  
 */
export async function getManyFanArtComments(filter: string = ""): Promise<FanArtCommentsResponse[]> {
  try {
    const results = await get("fanArtComments", { prompt: filter });
    return Array.isArray(results) ? results as FanArtCommentsResponse[] : [];
  } catch (error) {
    console.error(`Error getting FanArtComments collection at path 'fanArtComments': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in FanArtComments collection at fanArtComments
 
  Read Operation Details: Anyone can read fan art comments. Displayed publicly under each fan art submission.
  
 */
export function subscribeManyFanArtComments(callback: (data: FanArtCommentsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("fanArtComments", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as FanArtCommentsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to FanArtComments collection at path '${"fanArtComments"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all FanArtComments items from collection fanArtComments
 
  Read Operation Details: Anyone can read fan art comments. Displayed publicly under each fan art submission.
  
 */
export async function getAllFanArtComments(filter: string = ""): Promise<FanArtCommentsResponse[]> {
  try {
    const results = await get("fanArtComments", { prompt: filter });
    return Array.isArray(results) ? results as FanArtCommentsResponse[] : [];
  } catch (error) {
    console.error(`Error getting FanArtComments collection at path 'fanArtComments': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in FanArtComments collection at fanArtComments
 
  Read Operation Details: Anyone can read fan art comments. Displayed publicly under each fan art submission.
  
 */
export function subscribeAllFanArtComments(callback: (data: FanArtCommentsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("fanArtComments", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as FanArtCommentsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to FanArtComments collection at path '${"fanArtComments"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count FanArtComments items in collection fanArtComments.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countFanArtComments(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("fanArtComments", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting FanArtComments at path 'fanArtComments': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on FanArtComments items in collection fanArtComments.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateFanArtComments(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("fanArtComments", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating FanArtComments at path 'fanArtComments': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Admin or founder only. Can remove comments for moderation purposes.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteFanArtComments(commentId: string): Promise<boolean> {
  try {
    const succeeded = await set(`fanArtComments/${commentId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting FanArtComments: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for FanArtComments for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteFanArtComments(commentId: string): DocumentOperation {
  return { path: `fanArtComments/${commentId}`, document: null };
}

